import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authService } from '../../services/api'
import { User } from '../../models/User.model'
import i18n from '../../configs/i18n'

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
  permissions: [],
  roles: [],
  refreshToken: null,
  accessToken: null,
  // Estados para o fluxo de registro
  registrationEmail: null,
  registrationStep: 'form', // 'form', 'verification', 'completed'
  verificationLoading: false,
  resendLoading: false,
  // Estados para o fluxo de recuperação de senha
  resetPasswordEmail: null,
  resetPasswordStep: 'email', // 'email', 'code', 'password'
  codeVerified: false,
  resetPasswordLoading: false,
  verifyCodeLoading: false
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        // Verificar se é erro de conta não verificada
        if (response.data.obj && response.data.obj.verification_required) {
          return rejectWithValue({
            message: response.data.message || 'Conta não verificada',
            verification_required: true,
            validationErrors: response.data.obj
          })
        }
        
        return rejectWithValue({
          message: response.data.message || 'Login falhou',
          validationErrors: response.data.obj
        })
      }
      
      // Extrair dados do usuário da estrutura correta da API
      let userData = response.data.obj?.profile || response.data.userData
      let accessToken = response.data.obj?.accessToken || response.data.accessToken
      let refreshToken = response.data.obj?.refreshToken || response.data.refreshToken
      
      // Se não encontrou na estrutura obj, tentar na raiz
      if (!userData) {
        userData = response.data.profile || response.data.userData
      }
      if (!accessToken) {
        accessToken = response.data.accessToken
      }
      if (!refreshToken) {
        refreshToken = response.data.refreshToken
      }
      
      // Verificar se o token não é true ou uma string 'true'
      if (accessToken === true || accessToken === 'true') {
        console.error('❌ Token é true, tentando encontrar token real na resposta')
        // Tentar encontrar o token real em outras propriedades
        const allKeys = Object.keys(response.data)
        
        for (const key of allKeys) {
          const value = response.data[key]
          if (typeof value === 'string' && value.length > 50 && value.includes('.')) {
            accessToken = value
            break
          }
        }
        
        // Se ainda não encontrou, tentar na estrutura obj
        if (response.data.obj) {
          const objKeys = Object.keys(response.data.obj)
          
          for (const key of objKeys) {
            const value = response.data.obj[key]
            if (typeof value === 'string' && value.length > 50 && value.includes('.')) {
              accessToken = value
              break
            }
          }
        }
      }
      
      // Verificar se o token é válido
      if (!accessToken || accessToken === true || accessToken === 'true' || typeof accessToken !== 'string') {
        console.error('❌ Token inválido:', accessToken, 'Tipo:', typeof accessToken)
        throw new Error('Token inválido recebido da API')
      }
      
      // Verificar se o token tem pelo menos 50 caracteres (mínimo para um JWT)
      if (accessToken.length < 50) {
        throw new Error('Token muito curto recebido da API')
      }
      
      // Verificar se o token tem o formato JWT (3 partes separadas por ponto)
      if (!accessToken.includes('.')) {
        throw new Error('Token não tem formato JWT válido')
      }
      
      // Store tokens
      const dataToStore = {
        ...userData,
        accessToken,
        refreshToken,
        role: userData.roles?.[0] || userData.role || 'admin'
      }
      
      localStorage.setItem('userData', JSON.stringify(dataToStore))
      
      return {
        user: userData,
        accessToken,
        refreshToken,
        permissions: response.data.permissions || [],
        roles: userData.roles || []
      }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Login failed',
        validationErrors: error.response?.data?.obj,
        statusCode: error.response?.status,
        verificationRequired: error.response?.data?.obj?.verification_required || false
      })
    }
  }
)

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData)
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Registro falhou',
          validationErrors: response.data.obj
        })
      }
      
      return response.data
    } catch (error) {
      // Verificar se é erro 403 com verification_required
      if (error.response?.status === 403 && error.response?.data?.obj?.verification_required) {
        return rejectWithValue({
          message: error.response.data.message || 'Conta não verificada. Verifique seu email.',
          verification_required: true,
          statusCode: 403,
          validationErrors: error.response.data.obj
        })
      }
      
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro de conexão no login',
        statusCode: error.response?.status,
        validationErrors: error.response?.data?.obj
      })
    }
  }
)

export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (token, { rejectWithValue }) => {
    try {
      const response = await authService.googleLogin(token)

      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Login com Google falhou'
        })
      }

      if (response.data.obj?.google_registration_required) {
        return {
          google_registration_required: true,
          google_data: response.data.obj.google_data
        }
      }

      let userData = response.data.obj?.profile
      let accessToken = response.data.obj?.accessToken
      let refreshToken = response.data.obj?.refreshToken

      if (!accessToken || typeof accessToken !== 'string' || accessToken.length < 50) {
        throw new Error('Token inválido recebido da API')
      }

      const dataToStore = {
        ...userData,
        accessToken,
        refreshToken,
        role: userData.roles?.[0] || 'doctor'
      }
      localStorage.setItem('userData', JSON.stringify(dataToStore))

      return {
        user: userData,
        accessToken,
        refreshToken,
        permissions: [],
        roles: userData.roles || []
      }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro ao fazer login com Google',
        statusCode: error.response?.status
      })
    }
  }
)

export const googleRegister = createAsyncThunk(
  'auth/googleRegister',
  async (token, { rejectWithValue }) => {
    try {
      const response = await authService.googleRegister(token)

      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Cadastro com Google falhou'
        })
      }

      let userData = response.data.obj?.profile
      let accessToken = response.data.obj?.accessToken
      let refreshToken = response.data.obj?.refreshToken

      if (!accessToken || typeof accessToken !== 'string' || accessToken.length < 50) {
        throw new Error('Token inválido recebido da API')
      }

      const dataToStore = {
        ...userData,
        accessToken,
        refreshToken,
        role: userData.roles?.[0] || 'doctor'
      }
      localStorage.setItem('userData', JSON.stringify(dataToStore))

      return {
        user: userData,
        accessToken,
        refreshToken,
        permissions: [],
        roles: userData.roles || []
      }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro ao cadastrar com Google',
        statusCode: error.response?.status
      })
    }
  }
)

export const verifyAccount = createAsyncThunk(
  'auth/verifyAccount',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyAccount({ email, code })
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Verificação falhou',
          codeExpired: response.data.obj?.code_expired || false
        })
      }
      
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro de conexão na verificação',
        codeExpired: error.response?.data?.obj?.code_expired || false
      })
    }
  }
)

export const resendVerificationCode = createAsyncThunk(
  'auth/resendVerificationCode',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.resendVerificationCode(email)
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Falha ao reenviar código'
        })
      }
      
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro de conexão ao reenviar código'
      })
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await authService.logout()
      // Limpar todos os dados de autenticação do localStorage
      localStorage.removeItem('userData')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      return true
    } catch (error) {
      // Even if logout fails on server, clear local storage
      localStorage.removeItem('userData')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      return true
    }
  }
)

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState()
      if (!auth.refreshToken) {
        throw new Error('No refresh token available')
      }
      
      const response = await authService.refreshToken(auth.refreshToken)
      
      // Update stored data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      userData.accessToken = response.data.accessToken
      localStorage.setItem('userData', JSON.stringify(userData))
      
      return {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken || auth.refreshToken
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed')
    }
  }
)

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email)
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Falha ao enviar código de recuperação',
          validationErrors: response.data.obj
        })
      }
      
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro ao enviar código de recuperação',
        validationErrors: error.response?.data?.obj
      })
    }
  }
)

export const verifyPasswordResetCode = createAsyncThunk(
  'auth/verifyPasswordResetCode',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyPasswordResetCode({ email, code })
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Código inválido',
          validationErrors: response.data.obj
        })
      }
      
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro ao validar código',
        validationErrors: error.response?.data?.obj
      })
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, code, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword({
        email,
        code,
        password,
        password_confirmation
      })
      
      // Verificar se há erro na resposta
      if (response.data.error) {
        return rejectWithValue({
          message: response.data.message || 'Falha ao redefinir senha',
          validationErrors: response.data.obj
        })
      }
      
      return response.data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Erro ao redefinir senha',
        validationErrors: error.response?.data?.obj
      })
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(profileData)
      const userData = response.data // Usar dados brutos em vez do objeto User
      
      // Update localStorage
      const storedData = JSON.parse(localStorage.getItem('userData') || '{}')
      const updatedData = { ...storedData, ...userData }
      localStorage.setItem('userData', JSON.stringify(updatedData))
      
      return userData
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Profile update failed')
    }
  }
)

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCredentials: (state, action) => {
      const { user, accessToken, refreshToken, permissions, roles } = action.payload
      state.user = user
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      state.permissions = permissions || []
      state.roles = roles || []
      state.isAuthenticated = true
    },
    clearCredentials: (state) => {
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.permissions = []
      state.roles = []
      state.isAuthenticated = false
      state.error = null
    },
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1
      state.lastLoginAttempt = new Date().toISOString()
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
      state.lastLoginAttempt = null
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setRegistrationEmail: (state, action) => {
      state.registrationEmail = action.payload
    },
    setRegistrationStep: (state, action) => {
      state.registrationStep = action.payload
    },
    resetRegistration: (state) => {
      state.registrationEmail = null
      state.registrationStep = 'form'
      state.verificationLoading = false
      state.resendLoading = false
      state.error = null
    },
    setResetPasswordEmail: (state, action) => {
      state.resetPasswordEmail = action.payload
    },
    setResetPasswordStep: (state, action) => {
      state.resetPasswordStep = action.payload
    },
    resetPasswordFlow: (state) => {
      state.resetPasswordEmail = null
      state.resetPasswordStep = 'email'
      state.codeVerified = false
      state.resetPasswordLoading = false
      state.verifyCodeLoading = false
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.permissions = action.payload.permissions || []
        state.roles = action.payload.roles || []
        state.isAuthenticated = true
        state.loginAttempts = 0
        state.lastLoginAttempt = null
        
        // Garantir que capabilities estão mapeadas corretamente no user
        if (action.payload.user && !action.payload.user.capabilities && !action.payload.user.dataCapabilities) {
          // Se não tem capabilities, criar estrutura básica
          if (action.payload.user.capabilities === undefined) {
            action.payload.user.capabilities = {
              physicalCreditCard: false,
              clinicPartner: false,
              instructorDiscount: 0,
              instructorId: null,
              dentalPrev: false,
              all: {}
            }
          }
        }
        
        const preferredLanguage = localStorage.getItem('userPreferredLanguage')
        if (preferredLanguage && ['pt', 'en', 'es'].includes(preferredLanguage)) {
          i18n.changeLanguage(preferredLanguage)
        } else {
          const userLocale = action.payload.user?.locale || action.payload.profile?.locale
          if (userLocale) {
            const localeToLang = { 'pt_BR': 'pt', 'en': 'en', 'es': 'es' }
            const language = localeToLang[userLocale] || 'en'
            i18n.changeLanguage(language)
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.loginAttempts += 1
        state.lastLoginAttempt = new Date().toISOString()
      })

      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.google_registration_required) return
        state.user = action.payload.user
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.permissions = action.payload.permissions || []
        state.roles = action.payload.roles || []
        state.isAuthenticated = true
        state.loginAttempts = 0
        state.lastLoginAttempt = null

        const preferredLanguage = localStorage.getItem('userPreferredLanguage')
        if (preferredLanguage && ['pt', 'en', 'es'].includes(preferredLanguage)) {
          i18n.changeLanguage(preferredLanguage)
        } else {
          const userLocale = action.payload.user?.locale || action.payload.profile?.locale
          if (userLocale) {
            const localeToLang = { 'pt_BR': 'pt', 'en': 'en', 'es': 'es' }
            const language = localeToLang[userLocale] || 'en'
            i18n.changeLanguage(language)
          }
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Google Register
      .addCase(googleRegister.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(googleRegister.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.permissions = action.payload.permissions || []
        state.roles = action.payload.roles || []
        state.isAuthenticated = true

        const preferredLanguage = localStorage.getItem('userPreferredLanguage')
        if (preferredLanguage && ['pt', 'en', 'es'].includes(preferredLanguage)) {
          i18n.changeLanguage(preferredLanguage)
        } else {
          const userLocale = action.payload.user?.locale || action.payload.profile?.locale
          if (userLocale) {
            const localeToLang = { 'pt_BR': 'pt', 'en': 'en', 'es': 'es' }
            const language = localeToLang[userLocale] || 'en'
            i18n.changeLanguage(language)
          }
        }
      })
      .addCase(googleRegister.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false
        state.user = null
        state.accessToken = null
        state.refreshToken = null
        state.permissions = []
        state.roles = []
        state.isAuthenticated = false
        state.error = null
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false
        // Still clear credentials even if logout fails
        state.user = null
        state.accessToken = null
        state.refreshToken = null
        state.permissions = []
        state.roles = []
        state.isAuthenticated = false
      })
      
      // Refresh token
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        // If refresh fails, clear credentials
        state.user = null
        state.accessToken = null
        state.refreshToken = null
        state.permissions = []
        state.roles = []
        state.isAuthenticated = false
      })
      
      // Forgot password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.resetPasswordEmail = action.payload.obj?.email
        state.resetPasswordStep = 'code'
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Verify password reset code
      .addCase(verifyPasswordResetCode.pending, (state) => {
        state.verifyCodeLoading = true
        state.error = null
      })
      .addCase(verifyPasswordResetCode.fulfilled, (state, action) => {
        state.verifyCodeLoading = false
        state.error = null
        state.codeVerified = true
        state.resetPasswordStep = 'password'
      })
      .addCase(verifyPasswordResetCode.rejected, (state, action) => {
        state.verifyCodeLoading = false
        state.error = action.payload
      })
      
      // Reset password
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true
        state.error = null
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordLoading = false
        state.error = null
        // Resetar fluxo após sucesso
        state.resetPasswordEmail = null
        state.resetPasswordStep = 'email'
        state.codeVerified = false
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false
        state.error = action.payload
      })
      
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.error = null
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.registrationEmail = action.payload.obj?.email
        state.registrationStep = 'verification'
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Verify account
      .addCase(verifyAccount.pending, (state) => {
        state.verificationLoading = true
        state.error = null
      })
      .addCase(verifyAccount.fulfilled, (state) => {
        state.verificationLoading = false
        state.error = null
        state.registrationStep = 'completed'
      })
      .addCase(verifyAccount.rejected, (state, action) => {
        state.verificationLoading = false
        state.error = action.payload
      })
      
      // Resend verification code
      .addCase(resendVerificationCode.pending, (state) => {
        state.resendLoading = true
        state.error = null
      })
      .addCase(resendVerificationCode.fulfilled, (state) => {
        state.resendLoading = false
        state.error = null
      })
      .addCase(resendVerificationCode.rejected, (state, action) => {
        state.resendLoading = false
        state.error = action.payload
      })
  }
})

// Export actions
export const {
  clearError,
  setCredentials,
  clearCredentials,
  incrementLoginAttempts,
  resetLoginAttempts,
  setLoading,
  setRegistrationEmail,
  setRegistrationStep,
  resetRegistration,
  setResetPasswordEmail,
  setResetPasswordStep,
  resetPasswordFlow
} = authSlice.actions

// Selectors
export const selectAuth = (state) => state.auth
export const selectCurrentUser = (state) => state.auth.user
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectAuthLoading = (state) => state.auth.loading
export const selectAuthError = (state) => state.auth.error
export const selectUserPermissions = (state) => state.auth.permissions
export const selectUserRoles = (state) => state.auth.roles
export const selectAccessToken = (state) => state.auth.accessToken
export const selectLoginAttempts = (state) => state.auth.loginAttempts

// Capabilities selectors
export const selectUserCapabilities = (state) => {
  const user = state.auth.user
  return user?.capabilities || user?.dataCapabilities || null
}

export const selectPhysicalCreditCard = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.physicalCreditCard || capabilities?.creditCard || false
}

export const selectClinicPartner = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.clinicPartner || false
}

export const selectInstructorDiscount = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.instructorDiscount || 0
}

export const selectInstructorId = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.instructorId || null
}

export const selectDentalPrev = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.dentalPrev || false
}

export const selectCapabilitiesAll = (state) => {
  const capabilities = selectUserCapabilities(state)
  return capabilities?.all || {}
}

// Helper selectors
export const selectCanAccess = (resource, action = 'read') => (state) => {
  const permissions = state.auth.permissions
  return permissions.some(permission => permission.resource === resource && permission.actions.includes(action))
}

export const selectHasRole = (role) => (state) => {
  return state.auth.roles.includes(role)
}

export const selectHasAnyRole = (roles) => (state) => {
  return roles.some(role => state.auth.roles.includes(role))
}

export default authSlice.reducer